/**
 * Раздел «Генерация»: шапка с состоянием партнёра + три вкладки.
 *
 * Шапка и переключение живут здесь, каждая вкладка — свой файл:
 * `PlayerCard.tsx` на 558 строк уже показывает, во что превращается раздел,
 * живущий одним файлом.
 *
 * Раздел ОБЯЗАН открываться и без ключа партнёра (`MUSEBOX_API_KEY`):
 * ключ появляется у владельца позже кода, а назначать шаблоны и смотреть
 * уровни нужно уже сейчас. Сервер это уже гарантирует (`/musebox/status`
 * отвечает `enabled: false`, а не 503) — здесь только не ломать вкладки,
 * когда каталог партнёра пуст.
 */

import { useCallback, useEffect, useState } from 'react'
import { request } from '../api/client.js'
import type { HeroineRow, HeroinesResponse, MuseboxStatus, TemplateRow, TemplatesResponse } from '../api/types.js'
import { PageHead } from '../components/Layout.js'
import { Card, Notice, Stat } from '../components/ui.js'
import { Heroines } from './generation/Heroines.js'
import { Levels } from './generation/Levels.js'
import { Templates } from './generation/Templates.js'

type Tab = 'levels' | 'templates' | 'heroines'

const TABS: { id: Tab; title: string }[] = [
  { id: 'levels', title: 'Уровни' },
  { id: 'templates', title: 'Шаблоны' },
  { id: 'heroines', title: 'Героини' },
]

export function Generation(): JSX.Element {
  const [tab, setTab] = useState<Tab>('levels')

  const [status, setStatus] = useState<MuseboxStatus | null>(null)
  const [statusError, setStatusError] = useState<string | null>(null)

  // Полный каталог БЕЗ фильтров — только для выпадающего списка на вкладке
  // «Уровни». У «Шаблонов» своя, независимая загрузка с поиском (см. её
  // файл) — делить состояние значило бы, что поиск там молча сужает список
  // здесь.
  const [templates, setTemplates] = useState<TemplateRow[]>([])
  const [templatesWarning, setTemplatesWarning] = useState<string | null>(null)

  const [heroines, setHeroines] = useState<HeroineRow[]>([])
  const [heroinesError, setHeroinesError] = useState<string | null>(null)

  // Одна «версия» на весь раздел: действие в любой вкладке (заказ, отметка
  // «проверено», фото) обязано обновить и шапку (очередь, счётчик
  // непроверенных), и список героинь. Раздел маленький — три независимых
  // счётчика были бы точнее, но не отличимы на практике.
  const [version, setVersion] = useState(0)
  const bump = useCallback(() => setVersion((v) => v + 1), [])

  useEffect(() => {
    let cancelled = false
    request<MuseboxStatus>('/admin/musebox/status')
      .then((data) => {
        if (cancelled) return
        setStatus(data)
        setStatusError(null)
      })
      .catch((err: Error) => {
        if (!cancelled) setStatusError(err.message)
      })
    return () => {
      cancelled = true
    }
  }, [version])

  useEffect(() => {
    let cancelled = false
    request<TemplatesResponse>('/admin/musebox/templates')
      .then((data) => {
        if (cancelled) return
        setTemplates(data.templates)
        setTemplatesWarning(data.warnings[0] ?? null)
      })
      .catch((err: Error) => {
        if (!cancelled) setTemplatesWarning(err.message)
      })
    return () => {
      cancelled = true
    }
  }, [version])

  useEffect(() => {
    let cancelled = false
    request<HeroinesResponse>('/admin/generation/heroines')
      .then((data) => {
        if (cancelled) return
        setHeroines(data.heroines)
        setHeroinesError(null)
      })
      .catch((err: Error) => {
        if (!cancelled) setHeroinesError(err.message)
      })
    return () => {
      cancelled = true
    }
  }, [version])

  return (
    <>
      <PageHead
        title="Generation"
        subtitle="Ролики к сценам заказываются у партнёра (Musebox) из фото героини. Раздел открыт и без ключа партнёра — шаблоны и уровни можно настраивать заранее."
      />

      <div className="stack">
        <StatusHeader status={status} error={statusError} />

        <div className="btn-group">
          {TABS.map((t) => (
            <button key={t.id} type="button" className={tab === t.id ? 'is-active' : undefined} onClick={() => setTab(t.id)}>
              {t.title}
            </button>
          ))}
        </div>

        {tab === 'levels' && (
          <Levels templates={templates} templatesWarning={templatesWarning} onChanged={bump} />
        )}
        {tab === 'templates' && <Templates />}
        {tab === 'heroines' && <Heroines heroines={heroines} error={heroinesError} onChanged={bump} />}
      </div>
    </>
  )
}

/**
 * Баланс, план, очередь и предупреждения партнёра. Без ключа сервер
 * отвечает `enabled: false`, а `warnings` уже содержит русское «интеграция
 * выключена» — просто показываем его, не подменяя своим текстом.
 */
function StatusHeader({ status, error }: { status: MuseboxStatus | null; error: string | null }): JSX.Element {
  return (
    <Card title="Партнёр" hint="Ключ появляется у владельца позже кода — раздел настраивается и без него.">
      {error !== null && <Notice tone="error">{error}</Notice>}

      {status === null ? (
        <p className="muted">Загрузка…</p>
      ) : (
        <div className="stack">
          <div className="stats">
            <Stat label="Баланс">{status.balance === null ? '—' : status.balance}</Stat>
            <Stat label="План">{status.plan?.name ?? '—'}</Stat>
            <Stat label="В очереди">{status.queue.queued + status.queue.running}</Stat>
            <Stat label="Готово у партнёра">{status.queue.ready}</Stat>
            <Stat label="Отказов">{status.queue.failed}</Stat>
            <Stat label="Не проверено">{status.unreviewed}</Stat>
          </div>
          {status.warnings.map((w) => (
            <Notice key={w} tone="warn">
              {w}
            </Notice>
          ))}
        </div>
      )}
    </Card>
  )
}
